import React, { useState, useEffect, useLayoutEffect, useCallback, useId, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChevronDown, ChevronLeft, CircleCheck, CircleX, Clock, Hourglass, Inbox, LifeBuoy, Lock, SendHorizontal,
  SlidersHorizontal, TriangleAlert, UserX, X,
} from 'lucide-react';
import PageHead from '../../components/PageHead/PageHead';
import SubTabs from '../../components/SubTabs/SubTabs';
import MetricCard from '../../components/MetricCard/MetricCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Dropdown from '../../components/Dropdown/Dropdown';
import Modal from '../../components/Modal/Modal';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import SearchInput from '../../components/SearchInput/SearchInput';
import Pagination from '../../components/Pagination/Pagination';
import { useHideBottomNav, useIsCompact } from '../../components/Layout/shellState';
import { initialsOf, avatarTone } from '../../components/Avatar/Avatar';
import { formatDateTime } from '../../utils/datetime';
import errorText from '../../utils/errorText';
import { formatWhenShort } from '../../components/Messaging/when';
import useThreadHistory from '../../components/Messaging/useThreadHistory';
import { useI18n } from '../../i18n/I18nContext';
import {
  getAdminTickets,
  getAdminTicketStats,
  getAdminTicket,
  updateAdminTicket,
  postAdminMessage,
  getAdminAgents,
} from '../../api/support';
import './SupportHub.css';

/* ── Helpers ────────────────────────────────────────────────────────── */

// Reply with Ctrl+Enter (⌘+Enter on a Mac): Enter alone is a line break, so a long answer is never
// sent half-written.
const SEND_KEYS = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '')
  ? '⌘ Enter'
  : 'Ctrl + Enter';

// The one system line the API writes into a ticket (an English sentence with the status as an enum).
const STATUS_CHANGED = /^Status changed to (OPEN|IN_PROGRESS|WAITING_USER|RESOLVED|CLOSED)$/;

// Scroll a list (not the page) to its end.
function scrollToEnd(list) {
  if (!list) return;
  if (typeof list.scrollTo === 'function') list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
  else list.scrollTop = list.scrollHeight;
}

// A reply box grows with what is typed until its CSS max-height, then scrolls (no resize grip).
function growToFit(box) {
  if (!box) return;
  box.style.height = 'auto';
  if (!box.scrollHeight) { box.style.height = ''; return; } // no layout (a test DOM): leave the CSS height
  box.style.height = `${box.scrollHeight + (box.offsetHeight - box.clientHeight)}px`;
}

const STATUS_TONE = {
  OPEN: 'info',
  IN_PROGRESS: 'brand',
  WAITING_USER: 'warning',
  RESOLVED: 'success',
  CLOSED: 'neutral',
};

const PRIORITY_TONE = {
  LOW: 'neutral',
  NORMAL: 'info',
  HIGH: 'warning',
  URGENT: 'danger',
};

const STATUSES = ['OPEN', 'IN_PROGRESS', 'WAITING_USER', 'RESOLVED', 'CLOSED'];
const CATEGORY_KEYS = ['COURSES', 'PAYMENT', 'TECHNICAL', 'ACCOUNT', 'OTHER'];
const PRIORITY_KEYS = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

function useStatusLabel(t) {
  return useCallback((s) => {
    const map = {
      OPEN: t('support.statusOpen'),
      IN_PROGRESS: t('support.statusInProgress'),
      WAITING_USER: t('support.statusWaiting'),
      RESOLVED: t('support.statusResolved'),
      CLOSED: t('support.statusClosed'),
    };
    return map[s] || s;
  }, [t]);
}

function usePriorityLabel(t) {
  return useCallback((p) => {
    const map = {
      LOW: t('support.priLow'),
      NORMAL: t('support.priNormal'),
      HIGH: t('support.priHigh'),
      URGENT: t('support.priUrgent'),
    };
    return map[p] || p;
  }, [t]);
}

function useCategoryLabel(t) {
  return useCallback((c) => {
    const map = {
      COURSES: t('support.catCourses'),
      PAYMENT: t('support.catPayment'),
      TECHNICAL: t('support.catTechnical'),
      ACCOUNT: t('support.catAccount'),
      OTHER: t('support.catOther'),
    };
    return map[c] || c;
  }, [t]);
}

/* ── Sub-components ─────────────────────────────────────────────────── */

function TicketRow({ ticket, isActive, onClick, t }) {
  const getStatusLabel = useStatusLabel(t);
  const getPriorityLabel = usePriorityLabel(t);
  const getCategoryLabel = useCategoryLabel(t);
  const unread = ticket.staffUnreadCount || 0;
  const customer = ticket.user?.name || ticket.user?.email || '';
  // "Normal" is the default: only a priority that stands out earns a badge.
  const showPriority = Boolean(ticket.priority) && ticket.priority !== 'NORMAL';

  return (
    <button
      type="button"
      className={`ui-list-row sup-ticket-row${isActive ? ' is-active' : ''}${unread ? ' sup-ticket-row--unread' : ''}`}
      aria-current={isActive ? 'true' : undefined}
      onClick={onClick}
    >
      <span className="ui-avatar" data-tone={avatarTone(ticket.user?.id || customer || ticket.id)} aria-hidden="true">
        {initialsOf(customer)}
      </span>
      <span className="ui-list-row__body sup-ticket-body">
        <span className="sup-ticket-top">
          <span className="sup-ticket-subject" title={ticket.subject}>{ticket.subject}</span>
          <span className="sup-ticket-aside">
            {unread > 0 && (
              <span className="ui-count sup-unread" title={t('support.unread', { count: unread })}>
                <span aria-hidden="true">{unread > 9 ? '9+' : unread}</span>
                <span className="visually-hidden">{t('support.unread', { count: unread })}</span>
              </span>
            )}
            <span
              className="ui-list-row__meta sup-ticket-time"
              title={ticket.lastMessageAt ? formatDateTime(ticket.lastMessageAt) : undefined}
            >
              {formatWhenShort(ticket.lastMessageAt, t)}
            </span>
          </span>
        </span>
        {ticket.lastMessagePreview && (
          <span className="ui-list-row__sub sup-ticket-preview">{ticket.lastMessagePreview}</span>
        )}
        <span className="sup-ticket-meta">
          <StatusBadge tone={STATUS_TONE[ticket.status] || 'neutral'}>{getStatusLabel(ticket.status)}</StatusBadge>
          {showPriority && (
            <StatusBadge tone={PRIORITY_TONE[ticket.priority] || 'neutral'}>{getPriorityLabel(ticket.priority)}</StatusBadge>
          )}
          {ticket.category && <StatusBadge tone="neutral">{getCategoryLabel(ticket.category)}</StatusBadge>}
          {customer && <span className="sup-ticket-who" title={customer}>{customer}</span>}
        </span>
      </span>
    </button>
  );
}

function MessageBubble({ msg, t }) {
  const getStatusLabel = useStatusLabel(t);
  const isUser = msg.authorRole === 'USER';
  const isSystem = msg.authorRole === 'SYSTEM';
  const isInternal = msg.isInternal;

  const authorName = msg.author
    ? (msg.author.name || msg.author.email || t('support.systemMsg'))
    : (isSystem ? t('support.systemMsg') : '?');

  if (isSystem) {
    const changed = STATUS_CHANGED.exec(msg.body || '');
    return (
      <div className="sup-msg sup-msg--system">
        <div className="sup-msg-body">
          <span className="sup-msg-system-label">{t('support.systemMsg')}: </span>
          {changed ? t('support.systemStatusChanged', { status: getStatusLabel(changed[1]) }) : msg.body}
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        'sup-msg',
        isUser ? 'sup-msg--user' : 'sup-msg--staff',
        isInternal ? 'sup-msg--internal' : '',
      ].filter(Boolean).join(' ')}
    >
      <span
        className="ui-avatar ui-avatar--sm"
        data-tone={isUser ? avatarTone(msg.author?.id || authorName) : 'brand'}
        aria-hidden="true"
      >
        {initialsOf(authorName)}
      </span>
      <div className="sup-msg-main">
        <div className="sup-msg-head">
          <span className="sup-msg-author">{authorName}</span>
          {isInternal && (
            <span className="ds-badge ds-badge-warning sup-internal-tag">
              <Lock aria-hidden="true" />
              {t('support.internalTag')}
            </span>
          )}
          <time className="sup-msg-time" dateTime={msg.createdAt}>{formatDateTime(msg.createdAt)}</time>
        </div>
        <div className="sup-msg-body">{msg.body}</div>
      </div>
    </div>
  );
}

// The panel is mounted per ticket (key={ticket.id}); `drafts` is the parent's ref to the
// unsent replies, so a reply typed for one ticket waits for it instead of following the
// agent to the next ticket they open.
function ThreadPanel({ ticket, onUpdate, onRead, onBack, agents, drafts, t }) {
  const switchId = useId();
  const controlsId = useId();
  // On a phone the three controls fold into one row, so the conversation keeps the screen.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(false);
  const [replyBody, setReplyBody] = useState(drafts.current[ticket.id]?.body || '');
  const [isInternal, setIsInternal] = useState(Boolean(drafts.current[ticket.id]?.isInternal));
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [statusVal, setStatusVal] = useState(ticket.status);
  const [priorityVal, setPriorityVal] = useState(ticket.priority);
  const [assigneeVal, setAssigneeVal] = useState(ticket.assigneeId || '');
  const messagesRef = useRef(null);
  const replyRef = useRef(null);
  const backRef = useRef(null);
  const loadSeq = useRef(0);
  const compactScreen = useIsCompact();
  // What the server last confirmed for the three controls, to put them back when a PATCH fails.
  const savedRef = useRef({
    status: ticket.status,
    priority: ticket.priority,
    assigneeId: ticket.assigneeId || '',
  });
  const getStatusLabel = useStatusLabel(t);
  const getPriorityLabel = usePriorityLabel(t);
  const getCategoryLabel = useCategoryLabel(t);

  const loadThread = useCallback(({ silent = false } = {}) => {
    const seq = ++loadSeq.current;
    if (!silent) setThreadLoading(true);
    setThreadError(false);
    getAdminTicket(ticket.id)
      .then((data) => {
        if (seq !== loadSeq.current) return;
        setThread(data);
        setStatusVal(data.status);
        setPriorityVal(data.priority);
        setAssigneeVal(data.assigneeId || '');
        savedRef.current = {
          status: data.status,
          priority: data.priority,
          assigneeId: data.assigneeId || '',
        };
        // Opening a request is what marks it read on the server: the list drops its counter too.
        if (onRead) onRead(ticket.id);
      })
      .catch((err) => {
        console.error('[SupportHub] Failed to load the conversation:', err);
        if (seq === loadSeq.current) setThreadError(true);
      })
      .finally(() => {
        if (seq === loadSeq.current) setThreadLoading(false);
      });
  }, [ticket.id, onRead]);

  useEffect(() => { loadThread(); }, [loadThread]);

  // Once the panel is gone, a thread still on its way has nowhere to land.
  useEffect(() => () => { loadSeq.current += 1; }, []);

  useEffect(() => {
    scrollToEnd(messagesRef.current);
  }, [thread]);

  // The box fits its text: a restored draft, typing, and the empty box after a send.
  useLayoutEffect(() => { growToFit(replyRef.current); }, [replyBody]);

  // On a phone the request has replaced the list, and the list took the keyboard focus with it: the
  // request starts at the way back (a button, so no on-screen keyboard opens).
  useEffect(() => {
    if (compactScreen && backRef.current) backRef.current.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the request opens
  }, []);

  const updateDraft = (body, internal) => {
    setReplyBody(body);
    setIsInternal(internal);
    if (body || internal) drafts.current[ticket.id] = { body, isInternal: internal };
    else delete drafts.current[ticket.id];
  };

  const handleSend = useCallback(async () => {
    const body = replyBody.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await postAdminMessage(ticket.id, { body, isInternal });
      setReplyBody('');
      setIsInternal(false);
      delete drafts.current[ticket.id];
      loadThread({ silent: true });
      onUpdate && onUpdate();
    } catch (err) {
      toast.error(errorText(err, t('support.sendError')));
    } finally {
      setSending(false);
    }
  }, [replyBody, isInternal, sending, ticket.id, drafts, loadThread, onUpdate, t]);

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey) || e.nativeEvent?.isComposing) return;
    e.preventDefault();
    handleSend();
  };

  const handleUpdate = useCallback(async (patch) => {
    if (updating) return;
    setUpdating(true);
    try {
      await updateAdminTicket(ticket.id, patch);
      savedRef.current = {
        status: patch.status ?? savedRef.current.status,
        priority: patch.priority ?? savedRef.current.priority,
        assigneeId: 'assigneeId' in patch ? patch.assigneeId || '' : savedRef.current.assigneeId,
      };
      loadThread({ silent: true });
      onUpdate && onUpdate();
    } catch (err) {
      // The PATCH did not land: the controls move back to what the server still has,
      // instead of showing the value the agent just picked as if it were saved.
      setStatusVal(savedRef.current.status);
      setPriorityVal(savedRef.current.priority);
      setAssigneeVal(savedRef.current.assigneeId);
      toast.error(errorText(err, t('support.updateError')));
    } finally {
      setUpdating(false);
    }
  }, [updating, ticket.id, loadThread, onUpdate, t]);

  const isClosed = (thread?.status || ticket.status) === 'CLOSED';
  const isResolved = (thread?.status || ticket.status) === 'RESOLVED';
  const user = ticket.user || {};
  const customer = user.name || user.email || '';
  const customerEmail = user.name && user.email ? user.email : '';
  // Only a student has a profile page to open.
  const profileTo = user.id && (!user.role || user.role === 'STUDENT') ? `/students/${encodeURIComponent(user.id)}` : null;

  const statusOptions = STATUSES.map((s) => ({ value: s, label: getStatusLabel(s) }));
  const priorityOptions = PRIORITY_KEYS.map((p) => ({ value: p, label: getPriorityLabel(p) }));
  const assigneeOptions = [
    { value: '', label: t('support.unassigned') },
    ...(agents || []).map((a) => ({ value: a.id, label: a.name || a.email })),
  ];
  // Whoever has the request is always one of the options (named from the request itself): the list
  // of agents may still be on its way, or may not hold someone who has since left the team.
  if (assigneeVal && !assigneeOptions.some((o) => o.value === assigneeVal)) {
    const assignedUser = thread?.assignee || ticket.assignee;
    assigneeOptions.push({
      value: assigneeVal,
      label: assignedUser?.name || assignedUser?.email || t('support.assigneeLabel'),
    });
  }
  const assigneeName = assigneeOptions.find((o) => o.value === assigneeVal)?.label || t('support.unassigned');

  return (
    <div className="sup-thread">
      {/* Header: the request, who asked, the quick actions, and the three things an agent changes */}
      <div className="sup-thread-top">
        <div className="sup-thread-title-row">
          {onBack && (
            <button
              ref={backRef}
              type="button"
              className="ds-btn ds-btn-secondary ds-btn-icon sup-back-btn"
              onClick={onBack}
              aria-label={t('support.backBtn')}
            >
              <ChevronLeft aria-hidden="true" />
            </button>
          )}
          <div className="sup-thread-heading">
            <h2 className="sup-thread-title">{ticket.subject}</h2>
            <p className="sup-thread-sub">
              {customer && (profileTo ? (
                <Link className="sup-thread-customer" to={profileTo} title={t('support.openProfile')}>{customer}</Link>
              ) : (
                <span className="sup-thread-customer">{customer}</span>
              ))}
              {customerEmail && <span className="sup-thread-email" title={customerEmail}>{customerEmail}</span>}
              {ticket.category && (
                <span className="ds-badge ds-badge-neutral sup-thread-category">{getCategoryLabel(ticket.category)}</span>
              )}
            </p>
          </div>
          <div className="sup-thread-actions">
            {!isResolved && !isClosed && (
              <button
                type="button"
                className="ds-btn ds-btn-secondary ds-btn-sm sup-action"
                disabled={updating}
                aria-label={t('support.resolveBtn')}
                onClick={() => handleUpdate({ status: 'RESOLVED' })}
              >
                <CircleCheck aria-hidden="true" />
                <span className="sup-action-label">{t('support.resolveBtn')}</span>
              </button>
            )}
            {!isClosed && (
              <button
                type="button"
                className="ds-btn ds-btn-ghost ds-btn-sm sup-action"
                disabled={updating}
                aria-label={t('support.closeBtn')}
                onClick={() => handleUpdate({ status: 'CLOSED' })}
              >
                <CircleX aria-hidden="true" />
                <span className="sup-action-label">{t('support.closeBtn')}</span>
              </button>
            )}
          </div>
        </div>

        <button
          type="button"
          className="sup-details-toggle"
          aria-expanded={detailsOpen}
          aria-controls={controlsId}
          onClick={() => setDetailsOpen((open) => !open)}
        >
          <span className="sup-details-summary">
            <StatusBadge tone={STATUS_TONE[statusVal] || 'neutral'}>{getStatusLabel(statusVal)}</StatusBadge>
            {priorityVal && priorityVal !== 'NORMAL' && (
              <StatusBadge tone={PRIORITY_TONE[priorityVal] || 'neutral'}>{getPriorityLabel(priorityVal)}</StatusBadge>
            )}
            <span className="sup-details-assignee">{assigneeName}</span>
          </span>
          <span className="visually-hidden">{t('support.detailsToggle')}</span>
          <ChevronDown className="sup-details-chevron" aria-hidden="true" />
        </button>

        <div id={controlsId} className={`sup-thread-controls${detailsOpen ? ' is-open' : ''}`}>
          <div className="sup-control-group">
            <span className="sup-control-label">{t('support.statusLabel')}</span>
            <Dropdown
              size="sm"
              value={statusVal}
              options={statusOptions}
              disabled={updating}
              ariaLabel={t('support.statusLabel')}
              onChange={(value) => {
                setStatusVal(value);
                handleUpdate({ status: value });
              }}
            />
          </div>
          <div className="sup-control-group">
            <span className="sup-control-label">{t('support.priorityLabel')}</span>
            <Dropdown
              size="sm"
              value={priorityVal}
              options={priorityOptions}
              disabled={updating}
              ariaLabel={t('support.priorityLabel')}
              onChange={(value) => {
                setPriorityVal(value);
                handleUpdate({ priority: value });
              }}
            />
          </div>
          <div className="sup-control-group">
            <span className="sup-control-label">{t('support.assigneeLabel')}</span>
            <Dropdown
              size="sm"
              value={assigneeVal}
              options={assigneeOptions}
              disabled={updating}
              ariaLabel={t('support.assigneeLabel')}
              onChange={(value) => {
                setAssigneeVal(value);
                handleUpdate({ assigneeId: value || null });
              }}
            />
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="sup-messages" ref={messagesRef} role="log" aria-live="polite" aria-label={ticket.subject} tabIndex={0}>
        {threadLoading && <LoadingState skeleton="list" rows={3} compact />}
        {threadError && !threadLoading && (
          <PageEmptyState
            variant="error"
            size="lg"
            title={t('support.threadLoadError')}
            onRetry={() => loadThread()}
          />
        )}
        {!threadLoading && !threadError && thread && (
          (!thread.messages || thread.messages.length === 0) ? (
            <PageEmptyState variant="inbox" size="lg" title={t('support.noMessages')} />
          ) : (
            thread.messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} t={t} />
            ))
          )
        )}
      </div>

      {/* Reply box */}
      <div className="sup-reply-box">
        <textarea
          ref={replyRef}
          className={`sup-reply-textarea${isInternal ? ' sup-reply-textarea--internal' : ''}`}
          placeholder={t('support.replyPlaceholder')}
          aria-label={t('support.replyPlaceholder')}
          value={replyBody}
          onChange={(e) => updateDraft(e.target.value, isInternal)}
          onKeyDown={handleKeyDown}
          rows={3}
          readOnly={sending}
        />
        <div className="sup-reply-footer">
          {/* The whole row is the switch's label: a 28px switch alone is a small target on a phone */}
          <label className="sup-internal-toggle" htmlFor={switchId}>
            <input
              id={switchId}
              type="checkbox"
              role="switch"
              className="ui-switch"
              checked={isInternal}
              aria-labelledby={`${switchId}-label`}
              aria-describedby={`${switchId}-hint`}
              onChange={(e) => updateDraft(replyBody, e.target.checked)}
            />
            <span className="sup-internal-copy">
              <span className="sup-internal-label" id={`${switchId}-label`}>{t('support.internalToggle')}</span>
              <span className="sup-internal-hint" id={`${switchId}-hint`}>{t('support.internalHint')}</span>
            </span>
          </label>
          <div className="sup-send">
            <span className="sup-send-hint">{t('support.sendHint', { keys: SEND_KEYS })}</span>
            <button
              type="button"
              className={`ds-btn ds-btn-primary sup-send-btn${sending ? ' is-loading' : ''}`}
              disabled={!replyBody.trim() || sending}
              onClick={handleSend}
            >
              <SendHorizontal aria-hidden="true" />
              <span className="sup-send-label">{t('support.sendBtn')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────── */

const STATUS_TABS = ['ALL', ...STATUSES];
const TICKETS_PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 300;

function SupportHub() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const activeTab = STATUS_TABS.includes(params.get('status')) ? params.get('status') : 'ALL';

  // Stats
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);

  // Ticket list
  const [tickets, setTickets] = useState([]);
  const [ticketsMeta, setTicketsMeta] = useState({ total: 0, page: 1, totalPages: 1 });
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Page of the list. It belongs to one set of filters: when any of them changes it starts over
  // at 1, set while rendering so no request goes out for the old page first, and switching a
  // filter back does not bring the old page back.
  const filterKey = [activeTab, debouncedSearch, category, priority, assigneeFilter].join('|');
  const [pageState, setPageState] = useState({ key: filterKey, page: 1 });
  if (pageState.key !== filterKey) setPageState({ key: filterKey, page: 1 });
  const page = pageState.key === filterKey ? pageState.page : 1;
  const setPage = (next) => setPageState({ key: filterKey, page: next });

  // Agents (for the assignee controls)
  const [agents, setAgents] = useState([]);

  // Selected ticket
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  // The list is ordered by last activity, so a reply lifts its ticket to page 1 and a status
  // change can take it out of the filter: the open ticket keeps the last row listed for it, so
  // its thread stays open instead of closing under the agent when the reloaded page lacks it.
  const openRowRef = useRef(null);
  const listedTicket = tickets.find((tk) => tk.id === selectedTicketId) || null;
  if (listedTicket) openRowRef.current = listedTicket;
  const selectedTicket = listedTicket
    || (selectedTicketId && openRowRef.current?.id === selectedTicketId ? openRowRef.current : null);
  const [mobileShowThread, setMobileShowThread] = useState(false);
  // Replies typed but not sent, by ticket id (see ThreadPanel)
  const draftsRef = useRef({});
  const statsRequestRef = useRef(0);
  const ticketsRequestRef = useRef(0);

  // A phone gives the whole screen to an open thread: the tab bar steps aside.
  useHideBottomNav(mobileShowThread);

  // On a desktop the card is nearly a screen tall under the header and the numbers: opening a
  // request brings the card up under the bar, so the whole conversation is in view.
  const compact = useIsCompact();
  const splitRef = useRef(null);
  useEffect(() => {
    const card = splitRef.current;
    if (!selectedTicketId || compact || !card || typeof card.scrollIntoView !== 'function') return;
    const calm = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    card.scrollIntoView({ block: 'start', behavior: calm ? 'auto' : 'smooth' });
  }, [selectedTicketId, compact]);

  const getCategoryLabel = useCategoryLabel(t);
  const getPriorityLabel = usePriorityLabel(t);

  // Load stats
  const loadStats = useCallback(() => {
    const request = ++statsRequestRef.current;
    getAdminTicketStats()
      .then((data) => {
        if (request !== statsRequestRef.current) return;
        setStats(data);
        setStatsError(false);
      })
      .catch((err) => {
        console.error('[SupportHub] Failed to load the statistics:', err);
        if (request === statsRequestRef.current) setStatsError(true);
      });
  }, []);

  // Load agents
  const loadAgents = useCallback(() => {
    getAdminAgents()
      .then((data) => setAgents(Array.isArray(data) ? data : []))
      .catch(() => { /* non-fatal */ });
  }, []);

  // Load the current page of tickets. Only the newest request may land: a slow answer for an
  // older filter or page must not replace the list the agent is looking at. A `silent`
  // reload (after a reply or a status change) keeps the rows on screen while it runs.
  const loadTickets = useCallback(({ silent = false } = {}) => {
    const request = ++ticketsRequestRef.current;
    if (!silent) setTicketsLoading(true);
    setTicketsError(false);
    const reqParams = { page, limit: TICKETS_PER_PAGE };
    if (activeTab !== 'ALL') reqParams.status = activeTab;
    if (debouncedSearch) reqParams.search = debouncedSearch;
    if (category) reqParams.category = category;
    if (priority) reqParams.priority = priority;
    if (assigneeFilter === '__unassigned') reqParams.unassigned = true;
    else if (assigneeFilter) reqParams.assigneeId = assigneeFilter;

    getAdminTickets(reqParams)
      .then(({ data, meta }) => {
        if (request !== ticketsRequestRef.current) return;
        // The last ticket of this page just left the filter: step back to a page that exists.
        if (data.length === 0 && page > 1) {
          setPageState({ key: filterKey, page: Math.max(1, Math.min(page - 1, meta.totalPages)) });
          return;
        }
        setTickets(data);
        setTicketsMeta(meta);
      })
      .catch((err) => {
        console.error('[SupportHub] Failed to load the tickets:', err);
        if (request === ticketsRequestRef.current) setTicketsError(true);
      })
      .finally(() => {
        if (request === ticketsRequestRef.current) setTicketsLoading(false);
      });
  }, [page, filterKey, activeTab, debouncedSearch, category, priority, assigneeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { loadStats(); loadAgents(); }, [loadStats, loadAgents]);
  useEffect(() => { loadTickets(); }, [loadTickets]);

  // A reply or a status change moves the ticket in the list and in the counters. It may finish
  // after the agent has turned the page or changed a filter, so it reloads through the latest
  // loadTickets (what is on screen now), not the one it started with.
  const loadTicketsRef = useRef(loadTickets);
  loadTicketsRef.current = loadTickets;
  const refreshAfterChange = () => {
    loadTicketsRef.current({ silent: true });
    loadStats();
  };

  // The server marks a request read the moment its thread is opened: the row stops counting.
  const markTicketRead = useCallback((id) => {
    setTickets((prev) => (
      prev.some((tk) => tk.id === id && tk.staffUnreadCount)
        ? prev.map((tk) => (tk.id === id ? { ...tk, staffUnreadCount: 0 } : tk))
        : prev
    ));
  }, []);

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'ALL') next.delete('status');
    else next.set('status', key);
    setParams(next, { replace: true });
    setSelectedTicketId(null);
    setMobileShowThread(false);
  };

  // Back to the list. A phone's own Back button asks for this too (see useThreadHistory), so it has
  // to be safe with nothing open: an entry left behind by an earlier visit.
  const closeTicket = useCallback(() => {
    setMobileShowThread(false);
    setSelectedTicketId(null);
  }, []);
  const threadHistory = useThreadHistory('supportTicket', closeTicket);

  const handleSelectTicket = (id) => {
    setSelectedTicketId(id);
    setMobileShowThread(true);
    // On a phone the request is a screen of its own: it takes a history entry, so Back closes it.
    threadHistory.opened();
  };

  const TABS = STATUS_TABS.map((s) => ({
    key: s,
    label: {
      ALL: t('support.tabAll'),
      OPEN: t('support.tabOpen'),
      IN_PROGRESS: t('support.tabInProgress'),
      WAITING_USER: t('support.tabWaiting'),
      RESOLVED: t('support.tabResolved'),
      CLOSED: t('support.tabClosed'),
    }[s] || s,
  }));

  // What the filters are set to, as chips under the search box and as options in the dialog.
  const agentName = (id) => {
    const agent = agents.find((a) => a.id === id);
    return agent ? (agent.name || agent.email) : t('support.filterAssignee');
  };
  const activeFilters = [
    category && {
      key: 'category',
      label: `${t('support.filterCategory')}: ${getCategoryLabel(category)}`,
      clear: () => setCategory(''),
    },
    priority && {
      key: 'priority',
      label: `${t('support.filterPriority')}: ${getPriorityLabel(priority)}`,
      clear: () => setPriority(''),
    },
    assigneeFilter && {
      key: 'assignee',
      label: `${t('support.filterAssignee')}: ${assigneeFilter === '__unassigned' ? t('support.filterUnassigned') : agentName(assigneeFilter)}`,
      clear: () => setAssigneeFilter(''),
    },
  ].filter(Boolean);
  const hasFilters = activeFilters.length > 0 || Boolean(debouncedSearch) || activeTab !== 'ALL';

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setCategory('');
    setPriority('');
    setAssigneeFilter('');
    if (activeTab !== 'ALL') setTab('ALL');
  };

  const allOption = { value: '', label: t('support.filterAll') };
  const categoryOptions = [allOption, ...CATEGORY_KEYS.map((c) => ({ value: c, label: getCategoryLabel(c) }))];
  const priorityOptions = [allOption, ...PRIORITY_KEYS.map((p) => ({ value: p, label: getPriorityLabel(p) }))];
  const assigneeOptions = [
    allOption,
    { value: '__unassigned', label: t('support.filterUnassigned') },
    ...agents.map((a) => ({ value: a.id, label: a.name || a.email })),
  ];

  // `resolved` counts every ticket sitting in RESOLVED; the tile is about today only.
  const resolvedToday = stats?.resolvedToday ?? 0;
  const kpis = [
    { key: 'open', title: t('support.statsOpen'), value: stats?.open ?? 0, icon: <Inbox />, tone: 'info' },
    { key: 'progress', title: t('support.statsInProgress'), value: stats?.inProgress ?? 0, icon: <Clock />, tone: 'brand' },
    { key: 'waiting', title: t('support.statsWaiting'), value: stats?.waitingUser ?? 0, icon: <Hourglass />, tone: 'warning' },
    { key: 'resolved', title: t('support.statsResolved'), value: resolvedToday, icon: <CircleCheck />, tone: 'success' },
    { key: 'unassigned', title: t('support.statsUnassigned'), value: stats?.unassigned ?? 0, icon: <UserX />, tone: 'coral' },
  ];

  let listBody;
  // Rows that are already on screen stay (dimmed) while the next answer is on its way: a search or a
  // page turn does not blank the list. The skeleton is for the first load.
  if (ticketsLoading && tickets.length === 0) {
    listBody = <LoadingState skeleton="list" rows={6} compact />;
  } else if (ticketsError) {
    listBody = (
      <PageEmptyState
        variant="error"
        size="lg"
        title={t('support.loadError')}
        onRetry={() => loadTickets()}
      />
    );
  } else if (tickets.length === 0) {
    // A filtered list that comes up empty is one row in the list (the split stays, so the agent can
    // change the filter); no request at all is a state across the whole card.
    listBody = hasFilters ? (
      <InlineEmpty
        className="sup-no-match"
        kind="search"
        title={t('support.noTickets')}
        description={t('support.noTicketsDesc')}
        action={(
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={clearFilters}>
            {t('support.filtersClear')}
          </button>
        )}
      />
    ) : (
      <PageEmptyState
        variant="tickets"
        size="lg"
        title={t('support.emptyTitle')}
        description={t('support.emptyDesc')}
      />
    );
  } else {
    listBody = (
      <div className={`sup-ticket-rows${ticketsLoading ? ' is-busy' : ''}`}>
        {tickets.map((tk) => (
          <TicketRow
            key={tk.id}
            ticket={tk}
            isActive={tk.id === selectedTicketId}
            onClick={() => handleSelectTicket(tk.id)}
            t={t}
          />
        ))}
      </div>
    );
  }

  // The first answer is still on its way: the right pane waits too, instead of drawing "select a
  // request" for a frame and taking it away again.
  const firstLoad = ticketsLoading && tickets.length === 0;
  // No request at all (or the list would not load) and nothing open: one state across the whole card,
  // not an empty list beside a "select a request" pane and a search box for nothing. An empty tab or
  // an empty search keeps the split, because there the agent still has something to change.
  const listOnly = !ticketsLoading && tickets.length === 0 && !selectedTicketId && (ticketsError || !hasFilters);

  return (
    <div
      className={`sup-page${mobileShowThread ? ' is-thread-open' : ''}`}
      data-page={compact && mobileShowThread ? 'fill' : undefined}
    >
      {/* Header */}
      <PageHead icon={<LifeBuoy />} title={t('support.title')} description={t('support.subtitle')} />

      {/* Numbers */}
      {statsError ? (
        <InlineEmpty
          surface="card"
          className="sup-stats-error"
          icon={<TriangleAlert />}
          tone="danger"
          title={t('support.statsLoadError')}
          action={(
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={loadStats}>
              {t('support.retry')}
            </button>
          )}
        />
      ) : (
        <div className="kpi-row sup-kpis">
          {kpis.map(({ key, title, value, icon, tone }) => (
            <MetricCard key={key} title={title} value={value} icon={icon} tone={tone} loading={!stats} compact />
          ))}
        </div>
      )}

      {/* Status tabs */}
      <SubTabs className="sup-tabs" tabs={TABS} active={activeTab} onChange={setTab} ariaLabel={t('support.title')} />

      {/* One card, two panes: the requests, and the open conversation */}
      <div ref={splitRef} className={`sup-split section-card ui-split${mobileShowThread ? ' has-thread' : ''}${listOnly ? ' is-list-only' : ''}`}>
        <section className="sup-list-pane">
          {!listOnly && (
            <div className="sup-list-head">
              <div className="sup-list-tools">
                <SearchInput
                  className="sup-search"
                  value={search}
                  onChange={setSearch}
                  placeholder={t('support.searchPlaceholder')}
                />
                <button
                  type="button"
                  className="ds-btn ds-btn-secondary ds-btn-icon sup-filter-btn"
                  aria-label={t('support.filters')}
                  aria-haspopup="dialog"
                  onClick={() => setFiltersOpen(true)}
                >
                  <SlidersHorizontal aria-hidden="true" />
                  {activeFilters.length > 0 && <span className="ui-count sup-filter-count" aria-hidden="true">{activeFilters.length}</span>}
                </button>
              </div>
              {activeFilters.length > 0 && (
                <div className="ui-chip-row sup-active-filters">
                  {activeFilters.map((filter) => (
                    <span key={filter.key} className="ui-chip ui-chip--removable is-active">
                      {filter.label}
                      <button type="button" className="ui-chip__x" aria-label={t('common.clear')} onClick={filter.clear}>
                        <X aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="sup-ticket-list" aria-busy={ticketsLoading}>{listBody}</div>

          {ticketsMeta.totalPages > 1 && (
            <div className="sup-ticket-pager">
              <Pagination
                page={page}
                totalPages={ticketsMeta.totalPages}
                total={ticketsMeta.total}
                limit={TICKETS_PER_PAGE}
                onPrev={() => setPage(page - 1)}
                onNext={() => setPage(page + 1)}
              />
            </div>
          )}
        </section>

        {!listOnly && (
          <section className="sup-thread-pane">
            {selectedTicket ? (
              <ThreadPanel
                key={selectedTicket.id}
                ticket={selectedTicket}
                agents={agents}
                drafts={draftsRef}
                onUpdate={refreshAfterChange}
                onRead={markTicketRead}
                onBack={threadHistory.back}
                t={t}
              />
            ) : !firstLoad && (
              <PageEmptyState
                variant="tickets"
                size="lg"
                title={t('support.selectTicket')}
                description={t('support.selectTicketDesc')}
              />
            )}
          </section>
        )}
      </div>

      {/* Filters: three fields in a dialog (a bottom sheet on a phone) */}
      <Modal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title={t('support.filtersTitle')}
        size="sm"
        footer={(
          <>
            {activeFilters.length > 0 && (
              <button
                type="button"
                className="ds-btn ds-btn-secondary"
                onClick={() => { setCategory(''); setPriority(''); setAssigneeFilter(''); }}
              >
                {t('support.filtersClear')}
              </button>
            )}
            <button type="button" className="ds-btn ds-btn-primary" onClick={() => setFiltersOpen(false)}>
              {t('support.filtersDone')}
            </button>
          </>
        )}
      >
        <div className="sup-filter-fields">
          <div className="ui-field">
            <span className="ui-label">{t('support.filterCategory')}</span>
            <Dropdown value={category} onChange={setCategory} options={categoryOptions} ariaLabel={t('support.filterCategory')} />
          </div>
          <div className="ui-field">
            <span className="ui-label">{t('support.filterPriority')}</span>
            <Dropdown value={priority} onChange={setPriority} options={priorityOptions} ariaLabel={t('support.filterPriority')} />
          </div>
          <div className="ui-field">
            <span className="ui-label">{t('support.filterAssignee')}</span>
            <Dropdown value={assigneeFilter} onChange={setAssigneeFilter} options={assigneeOptions} ariaLabel={t('support.filterAssignee')} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SupportHub;
